/**
 * Presentation-only pure helpers for the pre-game lobby.
 *
 * Same split as `tag.ts` and `round.ts` beside it: what the lobby panel *says*
 * is decided here and tested without a browser, and `lobby-hud.ts` only puts it
 * on screen.
 *
 * Everything here is cosmetic, and — as with the tagging HUD — that is a
 * stronger statement than it sounds. The server decides who the host is and
 * whether a round may begin; it re-checks both on every `Msg.Start` regardless
 * of what this file concluded. Hiding the button from a non-host is a courtesy
 * to an honest player, never a control.
 */

import {
  MIN_PLAYERS_TO_START,
  RoundPhase,
  TICK_RATE,
  type RoundPhaseId,
} from "@prop-hunt/shared";

/** One line of the roster. */
export interface LobbyMember {
  id: number;
  name: string;
  isHost: boolean;
  isYou: boolean;
}

/**
 * Everyone in the lobby, in join order as far as the client can tell it.
 *
 * Sorted by id rather than by anything truer, because id order *is* join order
 * until somebody leaves and frees a slot — and the client is never told
 * `joinSeq`. The host is marked from `hostId` rather than assumed to be first
 * for exactly that reason: after a departure the two disagree, and the server's
 * answer is the one that decides.
 *
 * `peers` is `Connection.peers`, which does not contain the local player, so
 * they are added here. A missing name falls back to the id, matching `nameOf`
 * in main.ts: a roster row that reads "player 3" is better than a lobby that
 * appears to have fewer people in it than it does.
 */
export function lobbyRoster(
  localId: number,
  localName: string,
  peers: ReadonlyMap<number, string>,
  hostId: number,
): LobbyMember[] {
  const members: LobbyMember[] = [];
  if (localId !== 0) {
    members.push({
      id: localId,
      name: localName,
      isHost: localId === hostId,
      isYou: true,
    });
  }
  for (const [id, name] of peers) {
    members.push({ id, name: name || `player ${id}`, isHost: id === hostId, isYou: false });
  }
  return members.sort((a, b) => a.id - b.id);
}

/** What the lobby panel should look like this frame. */
export interface LobbyView {
  /** Whether the panel belongs on screen at all. */
  visible: boolean;
  /** Whether this client gets the START button. Host only. */
  showStart: boolean;
  /** Whether pressing it would do anything. */
  startEnabled: boolean;
  /** The line under the roster. */
  note: string;
  /**
   * The level this lobby is on, or "" before the server has said.
   *
   * Shown to everyone, not just the host, and read-only for all of them: the
   * choice was made when the lobby was created and cannot be changed now. A
   * joiner who clicked an invite link never picked it at all, and telling them
   * where they have landed is the entire job of this field.
   */
  levelName: string;
}

export interface LobbyStats {
  phase: RoundPhaseId;
  /**
   * Whether the server has actually accepted us — i.e. a Welcome has landed.
   *
   * Without this the panel reads its own client as the only player in the
   * lobby and says so, which is a confident lie in exactly the case where the
   * socket was refused or is still retrying: "waiting for one more player"
   * beside a prompt that says the connection failed.
   */
  connected: boolean;
  playerCount: number;
  amHost: boolean;
  /** The host's name, for the line a non-host reads. Null if not known yet. */
  hostName: string | null;
  /** Seconds until the lobby starts itself, or null if nothing is pending. */
  secondsToAutoStart: number | null;
  /**
   * The level id from `Msg.Welcome`, or null before one has arrived.
   *
   * The id rather than a prettier name because the id *is* the name — the
   * registry asserts `GameMap.name === MAPS` key, so there is no second string
   * that could drift out of step with it.
   */
  levelId: string | null;
}

/**
 * The panel is a Warmup thing and only a Warmup thing.
 *
 * Not "hide it once the round starts" — `Warmup` is the only phase in which a
 * round can be started, so the same condition answers both. A lobby that falls
 * back to Warmup mid-session (everyone but one player left) gets the panel
 * again, which is right: it is a lobby again.
 */
export function lobbyView(stats: LobbyStats): LobbyView {
  const visible = stats.phase === RoundPhase.Warmup;
  const enoughPlayers = stats.playerCount >= MIN_PLAYERS_TO_START;
  return {
    visible,
    showStart: stats.amHost,
    startEnabled: stats.connected && stats.amHost && enoughPlayers,
    note: lobbyNote(stats, enoughPlayers),
    levelName: stats.levelId ?? "",
  };
}

function lobbyNote(stats: LobbyStats, enoughPlayers: boolean): string {
  // Outranks everything: none of the numbers below mean anything until the
  // server has said who we are. The prompt line carries the detail if this is
  // a refusal or a retry rather than the ordinary first second.
  if (!stats.connected) return "connecting…";

  if (!enoughPlayers) {
    // The one case where nobody can do anything but wait, so it outranks both
    // the countdown (which is not running) and the host line.
    const missing = MIN_PLAYERS_TO_START - stats.playerCount;
    return missing === 1
      ? "waiting for one more player — share the link"
      : `waiting for ${missing} more players — share the link`;
  }

  const countdown =
    stats.secondsToAutoStart === null ? "" : ` · starts on its own in ${stats.secondsToAutoStart}s`;

  if (stats.amHost) return `ready when you are${countdown}`;
  // Named rather than "the host": in a five-person lobby, which of them is
  // being waited on is the whole content of the sentence.
  const who = stats.hostName === null ? "the host" : stats.hostName;
  return `waiting for ${who} to start${countdown}`;
}

/**
 * Seconds until the auto-start, or null if none is pending.
 *
 * Rounded up so the last second reads 1 rather than 0, matching
 * `secondsRemaining` in round.ts — a countdown that sits on 0 for a second
 * looks stuck.
 *
 * Clamped at 0 rather than allowed to go negative: the server starts the round
 * on its own tick and the client's estimate can be a hair ahead, which would
 * otherwise show "starts in -1s" for a frame or two before the Msg.Round lands.
 */
export function secondsToAutoStart(startsAtTick: number, serverTick: number): number | null {
  if (startsAtTick <= 0) return null;
  const ticks = startsAtTick - serverTick;
  return ticks <= 0 ? 0 : Math.ceil(ticks / TICK_RATE);
}
